import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';

import { ALLOW_OFFER_DETAILS_PREFETCH } from '@/constants/prefetch';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import Link from 'next/link';
import { OfferExpireTime } from './offer-expire-time';

import { ValidationTypes } from '@/constants/offers';

import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import { EventTypes, SelectOfferModel } from '@/logic/types/data-layer-models';
import { triggerGAEvent } from '@/logic/shared/data-layer-events';
import { useRouter } from 'next/router';
import ImageBoxFuture from '../shared/image-box-future';

const OfferFallbackImg = '/icons/offers/offer-empty-state.svg';
const clockBlackIcon = '/icons/offers/clock-black-icon.svg';

export type OfferCardProps = {
  image: string;
  imageAlt?: string;
  slug: string;
  title: string;
  description: string;
  time: string;
  bankName: string;
  validationType: string;
};

export const OfferCard: FC<OfferCardProps> = ({
  image,
  slug,
  title,
  description,
  time,
  bankName,
  imageAlt,
  validationType,
}) => {
  const theme = useTheme();
  const { cityId: browserCityId, getCity } = useCity();
  const { locale, asPath } = useRouter();

  const triggerDataLayerEvent = (): void => {
    const data: SelectOfferModel = {
      event: EventTypes.SELECT_OFFER,
      offer_title: title,
      language: locale.toUpperCase() as 'AR' | 'EN',
      location: getCity(browserCityId)?.name,
      page_section: asPath,
      page_title: document.title,
      page_url: document.URL,
    };
    triggerGAEvent(data);
  };

  return (
    <Card
      component='article'
      sx={{
        background: theme.palette.layout.bgSecondary,
        boxShadow: theme.customShadows.cardShadow,
        borderRadius: '25px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ImageBoxFuture
        img={image}
        title={imageAlt || title}
        fallBackImg={OfferFallbackImg}
        layout='intrinsic'
        objectFit='contain'
        width='524px'
        height='294px'
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      />
      <CardContent
        sx={{
          p: { tablet: pxToRem(24), mobile: '24px' },
          pb: '0 !important',
        }}
      >
        <Typography
          component='h2'
          sx={{
            fontWeight: '700',
            fontSize: { tablet: pxToRem(28), mobile: '18px' },
            lineHeight: { tablet: pxToRem(32), mobile: '27px' },
            mb: { tablet: pxToRem(24), mobile: '16px' },
            color: theme.palette.grey[90],
            display: '-webkit-box',
            WebkitLineClamp: { tablet: '1', mobile: '2' },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {validationType === ValidationTypes.BIN ? `${bankName} - ${title}` : title}
        </Typography>
        <Typography
          component='p'
          sx={{
            fontWeight: '400',
            fontSize: { tablet: pxToRem(24), mobile: '16px' },
            lineHeight: '150%',
            mb: { tablet: pxToRem(12), mobile: '13px' },
            color: theme.palette.grey[50],
            display: '-webkit-box',
            WebkitLineClamp: { tablet: '3', mobile: '4' },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {description}
        </Typography>
      </CardContent>

      <Box
        sx={{
          marginTop: 'auto',
        }}
      >
        <Typography
          component='time'
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: { tablet: pxToRem(14), mobile: '13px' },
            color: theme.palette.grey[90],
            lineHeight: { tablet: pxToRem(24), mobile: '24px' },
            mb: { tablet: pxToRem(26), mobile: '18px' },
            maxHeight: { tablet: pxToRem(108), mobile: '96px' },
            fontWeight: 400,
            paddingLeft: { tablet: pxToRem(24), mobile: '24px' },
          }}
        >
          <ImageBoxFuture
            img={clockBlackIcon}
            sx={{
              width: '24px',
              height: '24px',
              marginRight: '4px',
            }}
          />
          <FormattedMessage id='my_credit_cards.expiry_date' />
          <OfferExpireTime time={time} />
        </Typography>

        <CardActions sx={{ justifyContent: 'center', mb: pxToRem(24), p: 0 }}>
          <Link
            href={{
              pathname: `/offers/${slug}`,
              query: { cityId: browserCityId },
            }}
            passHref
            prefetch={ALLOW_OFFER_DETAILS_PREFETCH}
          >
            <Button
              onClick={() => triggerDataLayerEvent()}
              variant='contained'
              sx={{
                fontSize: pxToRem(16),
                width: '178px',
                height: { tablet: pxToRem(56), mobile: '56px' },
                borderRadius: '16px',
                fontWeight: 700,
              }}
            >
              <FormattedMessage id='global.explore' />
            </Button>
          </Link>
        </CardActions>
      </Box>
    </Card>
  );
};

export default OfferCard;
